class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.closeTimer = null;
    this.closeAbort = null;

    // Click/Enter/Space still work (both are the same "click" event on <summary>),
    // but we intercept it so open AND close are both driven by us — that's what
    // lets the close play an animation instead of the native instant collapse.
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));

    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
      this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    }
  }

  onSummaryClick(event) {
    event.preventDefault();
    clearTimeout(this.closeTimer);
    if (this.mainDetailsToggle.open) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  onMouseEnter() {
    clearTimeout(this.closeTimer);
    // `open` stays true for the whole close animation (see closeMenu()), so checking only
    // `!open` misses the case where the pointer re-enters WHILE a close is still fading out —
    // that read "already open, nothing to do" and let the in-flight close finish anyway,
    // landing shut under the cursor a moment later. Re-entering during that window needs to
    // reopen too, same as re-entering after a full close.
    if (!this.mainDetailsToggle.open || this.closeAbort) this.openMenu();
  }

  onMouseLeave() {
    this.closeTimer = setTimeout(() => this.closeMenu(), 200);
  }

  openMenu() {
    // Only one mega menu open at a time. Switching straight from one to another used to
    // swap the panel's contents in one frame — killing the old one with `.open = false`
    // dropped it out of the box tree instantly, same native-<details> problem closeMenu()
    // exists to solve. Route it through closeMenu() instead so the old content fades out
    // while the new one fades in, both sitting in the same absolutely-positioned box (see
    // mp-site.css) — a crossfade, not a snap.
    document.querySelectorAll('header-menu').forEach((other) => {
      if (other !== this && other.mainDetailsToggle.open) other.closeMenu();
    });

    // A close on THIS menu already in flight (fast leave-then-return) needs cancelling, not
    // just overriding — otherwise its own transitionend/timeout still fires afterward and
    // closes the menu we just told to stay open.
    if (this.closeAbort) {
      this.closeAbort();
      this.closeAbort = null;
    }

    this.mainDetailsToggle.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
  }

  closeMenu() {
    if (!this.mainDetailsToggle.open) return;
    if (this.closeAbort) return; // already closing — avoid stacking a second timer/listener

    const details = this.mainDetailsToggle;
    const content = this.content;
    let settled = false;

    const finish = (event) => {
      if (event && (event.target !== content || event.propertyName !== 'opacity')) return;
      if (settled) return;
      settled = true;
      content.removeEventListener('transitionend', finish);
      clearTimeout(timer);
      details.open = false;
      content.classList.remove('mega-menu__content--closing');
      this.closeAbort = null;
    };

    // Exposed so openMenu() can cancel a close that's still fading out, without finishing it
    // (no `details.open = false` here — the menu is meant to stay open).
    this.closeAbort = () => {
      settled = true;
      content.removeEventListener('transitionend', finish);
      clearTimeout(timer);
      content.classList.remove('mega-menu__content--closing');
    };

    this.summary.setAttribute('aria-expanded', 'false');
    content.classList.add('mega-menu__content--closing');
    content.addEventListener('transitionend', finish);
    // Safety net: if the transition never fires (reduced motion, element
    // hidden mid-animation), still land in the closed state.
    var timer = setTimeout(finish, 400);
  }

  close() {
    this.closeMenu();
  }

  onToggle() {
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);

/* Every mega menu panel used to size to its own content, so the white background ended at a
   different point per menu. Jordan's call 2026-08-26: the height should come from the real
   Gaming/console menu's content specifically, not from whichever panel happens to measure
   tallest — Services' two short badges shouldn't be able to stretch the panel past what Gaming
   already needs. So `.mega-menu__content--reference-exempt` (set in header-mega-menu.liquid on
   the Services branch only) is measured — every panel still reads the resulting height via
   min-height — but excluded from setting it. Measuring is done by briefly forcing each closed
   <details> open, synchronously, so nothing paints in between. Recomputed on resize (debounced)
   since the grid/column layout inside a panel is responsive. */
function measureMegaMenuHeight() {
  var panels = document.querySelectorAll('.mega-menu > .mega-menu__content');
  if (!panels.length) return;

  // Clear the previous result before remeasuring. Every panel's own min-height reads this
  // variable, so measuring scrollHeight while it is still set to last time's answer measures
  // "at least as tall as last time" — a ratchet that only ever grows, and on a resize-triggered
  // recompute (or a second run for any reason) never comes back down even if content shrinks.
  document.documentElement.style.removeProperty('--mega-menu-height');

  var max = 0;
  panels.forEach(function (panel) {
    var details = panel.closest('details');
    var wasOpen = details.open;
    if (!wasOpen) details.open = true;
    if (!panel.classList.contains('mega-menu__content--reference-exempt')) {
      max = Math.max(max, panel.scrollHeight);
    }
    if (!wasOpen) details.open = false;
  });

  if (max > 0) {
    document.documentElement.style.setProperty('--mega-menu-height', max + 'px');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', measureMegaMenuHeight);
} else {
  measureMegaMenuHeight();
}

/* header.liquid loads component-mega-menu.css with the `media="print" onload="this.media='all'"`
   trick — the file that gives `.mega-menu__content` its `position: absolute` and gives
   `.mega-menu__list` its 6-column grid does not apply until that swap fires, which is a real
   network request racing everything else on the page. Measuring before it lands sees the
   unstyled fallback (a plain stacked list, no absolute positioning) — much taller than the
   real thing — and that inflated number was what stuck on the home page specifically: more
   images and app scripts competing for bandwidth there delay the swap past DOMContentLoaded far
   more often than on a lighter page. `window.load` waits for every resource, this stylesheet's
   swap included, so remeasuring there is guaranteed to see the final, correctly-styled layout —
   and the ratchet fix above (clearing the variable first) is what lets this correction shrink the
   height back down instead of only ever being able to grow it. */
window.addEventListener('load', measureMegaMenuHeight);

(function () {
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(measureMegaMenuHeight, 200);
  });
})();
